/**
 * ModelKeyPicker — typeahead input that searches the universe of
 * modelKeys with ebay_sold_listings data, plus a "popular" quick-pick
 * grid of the top-N by recent volume.
 *
 * Two backends:
 *   /v1/observe/dbview/market/modelKeys?q=&limit=    (search)
 *   /v1/observe/dbview/market/modelKeys/popular?…    (top by volume)
 *
 * Debounced 250ms on input change so typing doesn't fire a request per
 * keystroke. Popular list cached at component mount.
 */

import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Search, X as XIcon } from 'lucide-react';
import { searchMarketModelKeys, listPopularModelKeys } from '@/api/market';
import { cn } from '@/lib/cn';
import { txt } from '@/lib/format';

interface Props {
  value: string;
  onChange: (modelKey: string) => void;
  /** Window passed to the popular endpoint (defaults to 30d). */
  popularSinceMs?: number;
  /** Optional label rendered before the input. */
  label?: string;
}

const DAY = 24 * 60 * 60 * 1000;

export default function ModelKeyPicker({ value, onChange, popularSinceMs = 30 * DAY, label }: Props) {
  const [query, setQuery] = useState(value);
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(query), 250);
    return () => clearTimeout(id);
  }, [query]);

  const searchResults = useQuery({
    queryKey: ['market', 'modelKeys', 'search', debounced],
    queryFn:  () => searchMarketModelKeys({ q: debounced, limit: 20 }),
    enabled:  debounced.length > 0,
  });

  const popular = useQuery({
    queryKey: ['market', 'modelKeys', 'popular', popularSinceMs],
    queryFn:  () => listPopularModelKeys({ sinceMs: popularSinceMs, limit: 20 }),
  });

  const suggestions = useMemo(() => searchResults.data?.rows ?? [], [searchResults.data]);

  return (
    <div className="space-y-2">
      {label && <div className="text-xs text-slate-500">{label}</div>}
      <div className="relative">
        <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-500 pointer-events-none" />
        <input
          value={query}
          onChange={e => setQuery(e.target.value)}
          placeholder="search modelKey (iphone_15, galaxy_s24, ...)"
          className="w-full bg-slate-900 border border-slate-800 rounded pl-7 pr-7 py-1.5 text-sm font-mono placeholder-slate-600"
        />
        {query && (
          <button
            onClick={() => { setQuery(''); onChange(''); }}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-500 hover:text-white"
            aria-label="Clear"
          >
            <XIcon className="w-3.5 h-3.5" />
          </button>
        )}
      </div>

      {debounced && suggestions.length > 0 && (
        <div className="max-h-44 overflow-y-auto bg-slate-900 border border-slate-800 rounded text-xs font-mono">
          {suggestions.map(mk => (
            <button
              key={mk}
              onClick={() => { setQuery(mk); onChange(mk); }}
              className={cn(
                'w-full text-left px-2 py-1.5 hover:bg-slate-800/70',
                mk === value && 'bg-slate-800 text-emerald-300',
              )}
            >
              {mk}
            </button>
          ))}
        </div>
      )}

      <div className="space-y-1">
        <div className="text-[10px] uppercase tracking-wide text-slate-600">Popular this {Math.round(popularSinceMs / DAY)}d</div>
        <div className="flex flex-wrap gap-1">
          {(popular.data?.rows ?? []).slice(0, 12).map(p => (
            <button
              key={p.modelKey}
              onClick={() => { setQuery(p.modelKey); onChange(p.modelKey); }}
              className={cn(
                'px-2 py-0.5 rounded text-[11px] font-mono border transition',
                p.modelKey === value
                  ? 'bg-emerald-900/40 border-emerald-700 text-emerald-300'
                  : 'bg-slate-900 border-slate-800 text-slate-400 hover:text-white hover:border-slate-700',
              )}
              title={`${p.soldCount.toLocaleString()} sold`}
            >
              {txt(p.modelKey)}
              <span className="ml-1 text-slate-600">{p.soldCount}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
